// © 2015-2021 WDNA - All Rights Reserved

import _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import InputRange, { Range } from 'react-input-range';

import Select2, { DataSelect2, EventSelect2 } from 'wdna-select2';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { CompanyDO, LocationDO } from '../../../management/requests';
import { TypeSensorDO, LovDO } from '../../../configuration/requests';
import { SensorDO } from '../../requests';

import '../../../../../assets/css/input-range.css';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';

/**
 * Function to format the sensor label
 *
 * @param value value of the label to format
 * @returns the formatted value
 */
function formatLabel(value: number): string {
  const formatvalue: string = value
    .toFixed(2)
    .toString()
    .replace('.', ':')
    .replace(':5', ':3')
    .replace('24', '0');

  return formatvalue;
}

/**
 * Function to get latitude coordinate by default
 *
 * @returns default latitude value
 */
function latitudeDefault(): number | undefined {
  const host: string = window.location.hostname;
  let latitude: number | undefined;
  if (host === 'test.entropysmart.com') latitude = 39.634722047264056;
  return latitude;
}

/**
 * Function to get longitude coordinate by default
 *
 * @returns default longitude value
 */
function longitudeDefault(): number | undefined {
  const host: string = window.location.hostname;
  let longitude: number | undefined;
  if (host === 'test.entropysmart.com') longitude = 2.634530736359011;
  return longitude;
}

interface Props {
  duplicate: boolean;
  formValues: {
    companiesArray: CompanyDO[];
    typeSensorArray: TypeSensorDO[];
    locationArray: LocationDO[];
    statusArray: LovDO[];
    groupsArray: LovDO[];
    timetable: {
      min: number;
      max: number;
    };
    isNew: boolean;
    isChirpStack: boolean;
    isWeatherLink: boolean;
    data: Partial<SensorDO>;
  };
  openMap: () => void;
  openChirpStack: () => void;
  genEUI: () => void;
  loadType: () => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onChangeRange: (l: string, v: Range | number) => void;
  onChangeUpdate: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeLocation: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onChangeMultiple: (e: EventSelect2) => void;
}

/**
 * Function to generate a form component
 *
 * @param props properties of the component
 * @returns jsx element with the form
 */
export default function Form(props: Props): JSX.Element {
  const { isNew, isChirpStack, isWeatherLink, data, timetable } = props.formValues;
  const { companiesArray, typeSensorArray, locationArray, statusArray, groupsArray } = props.formValues;

  latitudeDefault();

  const userIsADM: boolean = hasLevel('role', ['ADM']);
  const day: Date = new Date();
  const isNotLorawan: boolean = _.isEmpty(data.chirpstack?.eui);
  const isWLRequired: boolean = (isWeatherLink && !_.isEmpty(data.weatherlink?.user));

  return (
    <div>
      <fieldset>
        <div className="row">
          <div className="col-6">
            <label className="label">
              <b>Code</b> &nbsp;
            </label>
            <input
              type="text"
              id="data.code"
              name="data.code"
              value={_.defaultTo(data.code, '')}
              onChange={props.onChange}
              className="input-mandatory"
              style={{ width: '80%' }}
              placeholder="Code"
              required
              readOnly={(!isNew && !props.duplicate) && !userIsADM}
            />
          </div>
          <div className="col-4">
            <label className="label">
              <b>Type</b> &nbsp; &nbsp;&nbsp;
            </label>
            <span className="mandatory">
              <Select2
                id="data.typeSensorId"
                name="data.typeSensorId"
                value={_.defaultTo(data.typeSensorId, '')}
                onChange={props.onChange}
                data-ctype="number"
                data={typeSensorArray.map((item: TypeSensorDO): DataSelect2 => {
                  return {
                    id: item.id,
                    text: `${item.typeModuleName}: [${item.code}] - ${item.name}`,
                  };
                })}
                options={{ placeholder: 'Select option' }}
                style={{ width: '99%' }}
                disabled={!isNew}
                required
              />
            </span>
          </div>
          <div className="col-1">
            <label className="label">&nbsp;&nbsp;</label>
            {!isNew && (
              <button
                type="button"
                onClick={props.loadType}
                className="btn btn-secondary"
                style={{ margin: '0px 0 0 5px', padding: '0 12px', height: '32px', width: '75%' }}
              >
                Edit
              </button>
            )}
          </div>
          <div className="col-1" />
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              <b>Name</b> &nbsp;&nbsp;
            </label>
            <input
              type="text"
              id="data.name"
              name="data.name"
              value={_.defaultTo(data.name, '')}
              onChange={props.onChange}
              className="input-new"
              style={{ width: '80%' }}
              placeholder="Name (autogenerated)"
              readOnly
            />
          </div>
          <div className="col-6">
            <label className="label">
              <b>Description</b> &nbsp;&nbsp;
            </label>
            <input
              type="text"
              id="data.description"
              name="data.description"
              value={_.defaultTo(data.description, '')}
              onChange={props.onChange}
              className="input-mandatory"
              style={{ width: '80%' }}
              placeholder="Description"
              required
            />
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              <b>Company</b> &nbsp;
            </label>
            <span className="mandatory">
              <Select2
                id="data.companyId"
                name="data.companyId"
                value={_.defaultTo(data.companyId, '')}
                onChange={props.onChange}
                data-ctype="number"
                data={companiesArray.map((item: CompanyDO): DataSelect2 => {
                  return { text: item.name, id: item.id };
                })}
                options={{ placeholder: 'Select option' }}
                style={{ width: '80%' }}
                required
              />
            </span>
          </div>
          <div className="col-6">
            <label className="label">
              <b>Status</b> &nbsp;
            </label>
            <span className="mandatory">
              <Select2
                id="data.status"
                name="data.status"
                value={_.defaultTo(data.status, '')}
                onChange={props.onChange}
                data={statusArray.map((item: LovDO): DataSelect2 => {
                  return { id: item.id, text: item.name };
                })}
                options={{ placeholder: 'Select option' }}
                style={{ width: '80%' }}
                required
              />
            </span>
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-1">
            <label className="label">
              <b>Time NC:</b>
            </label>
            <input
              type="number"
              id="data.nocxTime"
              name="data.nocxTime"
              value={data.nocxTime || 60}
              onChange={props.onChange}
              data-ctype="number"
              className="input-mandatory"
              placeholder="Time NC"
              style={{ width: '100px' }}
              required
            />
          </div>
          <div className="col-5">
            <label className="label">
              <b>Scheduling NC:</b>
            </label>
            <div style={{ width: '80%' }}>
              <InputRange
                name="timetable"
                value={timetable}
                onChange={(value: Range | number): void => props.onChangeRange('timetable', value)}
                maxValue={24}
                minValue={0}
                step={0.5}
                allowSameValues
                formatLabel={formatLabel}
              />
            </div>
          </div>
          <div className="col-1">
            <label className="label">
              <b>Date Install</b>
            </label>
            <input
              type="date"
              id="data.dateInstall"
              name="data.dateInstall"
              value={moment(data.dateInstall || day).format('YYYY-MM-DD')}
              onChange={props.onChange}
              className="input-mandatory wdna-fit-date-size"
              required
            />
          </div>
          <div className="col-1">
            <label className="label">
              <b>Date Online</b>
            </label>
            <input
              type="date"
              id="data.dateOnline"
              name="data.dateOnline"
              value={moment(data.dateOnline || day).format('YYYY-MM-DD')}
              onChange={props.onChange}
              className="input-mandatory wdna-fit-date-size"
              required
            />
          </div>
          <div className="col-1">
            <label className="label">
              <b>Date Offline</b>
            </label>
            <input
              type="date"
              id="data.dateOffline"
              name="data.dateOffline"
              value={data.dateOffline ? moment(data.dateOffline).format('YYYY-MM-DD') : ''}
              onChange={props.onChange}
              className="input-new wdna-fit-date-size"
            />
            &nbsp;&nbsp;
          </div>
          <div className="col-1">
            <label className="label">
              <b>Date Action</b>
            </label>
            <input
              type="date"
              id="data.dateAction"
              name="data.dateAction"
              value={data.dateAction ? moment(data.dateAction).format('YYYY-MM-DD') : ''}
              onChange={props.onChange}
              className="input-new wdna-fit-date-size"
            />
            &nbsp;&nbsp;
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              <b>Location</b> &nbsp; &nbsp;&nbsp;
            </label>
            <span className="mandatory">
              <Select2
                id="data.locationId"
                name="data.locationId"
                value={_.defaultTo(data.locationId, '')}
                onChange={props.onChangeLocation}
                data-ctype="number"
                data={locationArray.map((item: LocationDO): DataSelect2 => {
                  return { text: `${item.name} (${item.address})`, id: item.id };
                })}
                options={{ placeholder: 'Select option' }}
                style={{ width: '80%' }}
                required
              />
            </span>
          </div>
          <div className="col-2">
            <label className="label">
              <b>Latitude:</b>
            </label>
            <input
              type="number"
              id="data.latitude"
              name="data.latitude"
              value={_.defaultTo(data.latitude, latitudeDefault() || '')}
              onChange={props.onChange}
              data-ctype="number"
              className="input-mandatory"
              style={{ width: '95%' }}
              placeholder="Latitude"
              step="any"
              required
            />
          </div>
          <div className="col-2">
            <label className="label">
              <b>Longitude:</b>
            </label>
            <input
              type="number"
              id="data.longitude"
              name="data.longitude"
              value={_.defaultTo(data.longitude, longitudeDefault() || '')}
              onChange={props.onChange}
              data-ctype="number"
              className="input-mandatory"
              style={{ width: '95%' }}
              placeholder="Longitude"
              step="any"
              required
            />
          </div>
          <div className="col-1">
            <label className="label">&nbsp;</label>
            <button
              type="button"
              onClick={props.openMap}
              className="btn btn-secondary"
              style={{ margin: '0px 0 0 5px', padding: '0 22px', height: '31px', width: '75%' }}
            >
              Map
            </button>
          </div>
          <div className="col-1" />
        </div>
        <br />
        <div className="row">
          <div className="col-6">
            <label className="label">
              <b>Groups</b> &nbsp;&nbsp;&nbsp; &nbsp;&nbsp;
            </label>
            <Select2
              id="groupsArray"
              name="groupsArray"
              value={data.groupsList}
              onSelect={props.onChangeMultiple}
              onUnselect={props.onChangeMultiple}
              data={groupsArray.map((item: LovDO): DataSelect2 => {
                return { text: item.name, id: item.id };
              })}
              options={{ placeholder: 'Select option' }}
              style={{ width: '80%' }}
              multiple
            />
          </div>
          <div className="col-6" />
        </div>
        <br />
        <div className="row">
          <div className="col-4">
            <label className="label">
              <b>ChirpStack-EUI</b> &nbsp;&nbsp;
            </label>
            &emsp;&emsp;
            <input
              type="checkbox"
              id="isChirpStack"
              name="isChirpStack"
              checked={isChirpStack}
              onChange={props.onChangeUpdate}
              data-ctype="boolean"
              className="input-new"
            />
            &nbsp;&nbsp;
            <input
              type="text"
              id="data.chirpstack.eui"
              name="data.chirpstack.eui"
              value={_.defaultTo(data.chirpstack?.eui, '')}
              onChange={props.onChange}
              className={`input-${isChirpStack ? 'mandatory' : 'new'}`}
              style={{ width: '90%' }}
              placeholder="ChirpStack-EUI"
              required={isChirpStack}
            />
          </div>
          <div className="col-1">
            <label className="label">&nbsp;&nbsp;</label>
            <button
              type="button"
              onClick={props.genEUI}
              className="btn btn-secondary"
              style={{ margin: '0px 0 0 5px', padding: '0 15px', height: '31px', width: '75%' }}
            >
              Generate
            </button>
          </div>
          <div className="col-1" />
          <div className="col-4">
            <label className="label">
              <b>ChirpStack-KEY</b> &nbsp;&nbsp;
            </label>
            <input
              type="text"
              id="data.chirpstack.key"
              name="data.chirpstack.key"
              value={_.defaultTo(data.chirpstack?.key, '')}
              onChange={props.onChange}
              className={`input-${isChirpStack ? 'mandatory' : 'new'}`}
              style={{ width: '99%' }}
              placeholder="ChirpStack-KEY"
              readOnly={_.isEmpty(data.chirpstack?.eui)}
              required={isChirpStack}
            />
          </div>
          <div className="col-1">
            <label className="label">&nbsp;</label>
            <button
              type="button"
              onClick={props.openChirpStack}
              className="btn btn-secondary"
              style={{ margin: '0px 0 0 5px', padding: '0 22px', height: '31px', width: '75%' }}
              disabled={isNew || !data.chirpstack?.eui}
            >
              Queue
            </button>
          </div>
          <div className="col-1" />
        </div>
        <br />
        <div className="row">
          <div style={{ width: '100%' }}>
            <label className="label">
              <b>Comments</b> &nbsp;&nbsp;
            </label>
            <label className="input">
              <textarea
                id="data.comments"
                name="data.comments"
                value={_.defaultTo(data.comments, '')}
                onChange={props.onChange}
                className="input-new"
                style={{ width: '90%' }}
                rows={3}
              />
            </label>
          </div>
        </div>
        <br />
        { isNotLorawan && isWeatherLink && (
          <>
            <div className="row">
              <div className="col-2">
                <label className="label">
                  <b>WeatherLink User</b> &nbsp;&nbsp;
                </label>
                <input
                  type="text"
                  id="data.weatherlink.user"
                  name="data.weatherlink.user"
                  value={_.defaultTo(data.weatherlink?.user, '')}
                  onChange={props.onChange}
                  className={`input-${isWLRequired ? 'mandatory' : 'new'}`}
                  style={{ width: '90%' }}
                  placeholder="WeatherLink User"
                  required={isWLRequired}
                />
              </div>
              <div className="col-1">
                <label className="label">
                  <b>WheatherLink TxID</b> &nbsp;&nbsp;
                </label>
                <input
                  type="number"
                  id="data.weatherlink.txid"
                  name="data.weatherlink.txid"
                  value={_.defaultTo(data.weatherlink?.txid, '')}
                  onChange={props.onChange}
                  data-ctype="number"
                  className={`input-${isWLRequired ? 'mandatory' : 'new'}`}
                  style={{ width: '90%' }}
                  placeholder="WeatherLink TxID"
                  required={isWLRequired}
                />
              </div>
              <div className="col-1">
                <label className="label">
                  <b>WheatherLink WIND</b> &nbsp;&nbsp;
                </label>
                <input
                  type="number"
                  id="data.weatherlink.wind"
                  name="data.weatherlink.wind"
                  value={_.defaultTo(data.weatherlink?.wind, '')}
                  onChange={props.onChange}
                  data-ctype="number"
                  className={`input-${isWLRequired ? 'mandatory' : 'new'}`}
                  style={{ width: '90%' }}
                  placeholder="WeatherLink WIND"
                  required={isWLRequired}
                />
              </div>
              <div className="col-2">
                <label className="label">
                  <b>Send To MET</b> &nbsp;&nbsp;
                </label>
                <input
                  type="checkbox"
                  id="data.weatherlink.send"
                  name="data.weatherlink.send"
                  checked={data?.weatherlink?.send}
                  onChange={props.onChangeUpdate}
                  data-ctype="boolean"
                  className="input-new"
                />
              </div>
              <div className="col-2">
                <label className="label">
                  <b>API-Key</b> &nbsp;&nbsp;
                </label>
                <input
                  type="text"
                  id="data.weatherlink.apky"
                  name="data.weatherlink.apky"
                  value={_.defaultTo(data.weatherlink?.apky, '')}
                  onChange={props.onChange}
                  className={`input-${isWLRequired ? 'mandatory' : 'new'}`}
                  style={{ width: '90%' }}
                  placeholder="WeatherLink API-KEY"
                  required={isWLRequired}
                />
              </div>
              <div className="col-2">
                <label className="label">
                  <b>API-Secret</b> &nbsp;&nbsp;
                </label>
                <input
                  type="text"
                  id="data.weatherlink.apst"
                  name="data.weatherlink.apst"
                  value={_.defaultTo(data.weatherlink?.apst, '')}
                  onChange={props.onChange}
                  className={`input-${isWLRequired ? 'mandatory' : 'new'}`}
                  style={{ width: '90%' }}
                  placeholder="WeatherLink API-SECRET"
                  required={isWLRequired}
                />
              </div>
              <div className="col-1">
                <label className="label">
                  <b>History (min)</b> &nbsp;&nbsp;
                </label>
                <input
                  type="number"
                  id="data.weatherlink.hist"
                  name="data.weatherlink.hist"
                  value={_.defaultTo(data.weatherlink?.hist, '')}
                  onChange={props.onChange}
                  data-ctype="number"
                  className={`input-${isWLRequired ? 'mandatory' : 'new'}`}
                  style={{ width: '40%' }}
                  placeholder="History (min)"
                  required={isWLRequired}
                />
              </div>
            </div>
            <br />
          </>
        )}
      </fieldset>
    </div>
  );
}
